import { FC } from 'react'

import styled from 'styled-components'

import { PrimaryHint } from 'src/Components/PrimaryHint'
import { TikTokCampaignPricingModelEnum } from 'src/Types/campaigns'
import { MAX_MOBILE_WIDTH, variables } from 'src/Styled/variables'

import {
  clampPercentage,
  formatGoalValue,
  getCampaignGoalConfig,
} from './helpers'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.color.lightGrey};
  background-color: ${({ theme }) => theme.color.white};
  box-shadow: ${({ theme }) => theme.shadow.card};
  width: 100%;
  max-width: 784px;

  @media (max-width: ${MAX_MOBILE_WIDTH}) {
    padding: 16px;
  }
`

const HeaderSection = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  width: 100%;
`

const TextSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
`

const Title = styled.p`
  font-size: ${variables.fontSize18};
  font-weight: ${variables.fontWeight600};
  line-height: 1.5;
  color: ${({ theme }) => theme.color.darkGrey};
  margin: 0;
`

const Subtitle = styled.p`
  font-size: ${variables.fontSize14};
  font-weight: ${variables.fontWeight400};
  line-height: 1.5;
  color: ${({ theme }) => theme.color.darkGrey};
  margin: 0;
`

const Badge = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  padding: 0 6px;
  height: 24px;
  border-radius: 16px;
  background-color: ${({ theme }) => theme.color.lightGrey};
  flex-shrink: 0;
`

const BadgeIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  color: ${({ theme }) => theme.color.darkGrey};
`

const BadgeText = styled.p`
  font-size: ${variables.fontSize12};
  font-weight: ${variables.fontWeight400};
  line-height: 1.5;
  color: ${({ theme }) => theme.color.darkGrey};
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const ViewsProgressSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`

const ViewsCount = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  height: 27px;
  white-space: nowrap;
`

const StyledText = styled.p`
  font-size: ${variables.fontSize18};
  font-weight: ${variables.fontWeight600};
  line-height: 1.5;
  color: ${({ theme }) => theme.color.darkGrey};
  margin: 0;
`

const ViewsRange = styled.div`
  display: flex;
  gap: 2px;
  align-items: center;
`

const RangeText = styled.p`
  font-size: ${variables.fontSize14};
  font-weight: ${variables.fontWeight400};
  line-height: 1.5;
  color: ${({ theme }) => theme.color.darkGrey};
  margin: 0;
`

const ViewsLabel = styled.p`
  font-size: ${variables.fontSize14};
  font-weight: ${variables.fontWeight400};
  line-height: 1.5;
  color: ${({ theme }) => theme.color.darkGrey};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: center;
`

const ProgressBarWrapper = styled.div`
  width: 100%;
  height: 6px;
  border-radius: 16px;
  background-color: ${({ theme }) => theme.color.lightGrey};
  overflow: hidden;
`

const ProgressBar = styled.div<{ $percentage: number }>`
  height: 100%;
  border-radius: 16px;
  background-color: ${({ theme }) => theme.color.brandGreen};
  width: ${({ $percentage }) => $percentage}%;
  transition: width 0.3s ease;
`

export type CampaignGoalProps = {
  currentValue?: number
  minValue?: number
  maxValue?: number
  percentage?: number
  pricingModel?: TikTokCampaignPricingModelEnum
}

export const CampaignGoal: FC<CampaignGoalProps> = ({
  currentValue = 0,
  minValue = 0,
  maxValue = 0,
  percentage = 0,
  pricingModel = TikTokCampaignPricingModelEnum.FollowersBased,
}) => {
  const progressPercentage = clampPercentage(percentage)
  const {
    metricLabel,
    icon: IconComponent,
    subtitle,
    badgeLabel,
    hint,
  } = getCampaignGoalConfig(pricingModel)

  if (pricingModel === TikTokCampaignPricingModelEnum.FollowersBased) {
    return null
  }

  return (
    <Container>
      <HeaderSection>
        <TextSection>
          <Title>Campaign goal</Title>
          <Subtitle>{subtitle}</Subtitle>
        </TextSection>

        <Badge>
          <BadgeIcon>
            <IconComponent />
          </BadgeIcon>
          <BadgeText>{badgeLabel}</BadgeText>
        </Badge>
      </HeaderSection>

      <ViewsProgressSection>
        <ViewsCount>
          <StyledText>{formatGoalValue(currentValue)}</StyledText>
          <StyledText>/</StyledText>
          <ViewsRange>
            <RangeText>{formatGoalValue(minValue)}</RangeText>
            <RangeText>-</RangeText>
            <RangeText>{formatGoalValue(maxValue)}</RangeText>
          </ViewsRange>
          <ViewsLabel>
            {metricLabel}
            {hint ? <PrimaryHint>{hint}</PrimaryHint> : null}
          </ViewsLabel>
        </ViewsCount>
        <ProgressBarWrapper>
          <ProgressBar $percentage={progressPercentage} />
        </ProgressBarWrapper>
      </ViewsProgressSection>
    </Container>
  )
}
